import React, { useState, useEffect } from 'react';
import { Code, Smartphone, Globe, Shield, Zap, Layers, ChevronRight, Play, Pause, Terminal, Database, Cloud } from 'lucide-react';

const WebDevHero = () => {
  const [isAnimating, setIsAnimating] = useState(true);
  const [currentService, setCurrentService] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const services = [
    "Custom Web Applications",
    "Mobile App Development", 
    "E-commerce Solutions",
    "ERP Development",
    "Cloud Integration"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (isAnimating) {
        setCurrentService((prev) => (prev + 1) % services.length);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [isAnimating, services.length]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    // { icon: Shield, title: "Security First", desc: "Enterprise-grade security protocols" },
    { icon: Zap, title: "High Performance", desc: "Optimized for speed and scalability" },
    { icon: Layers, title: "Modern Architecture", desc: "Microservices and cloud-native design" }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background: 'linear-gradient(to bottom right, #f8fafc, #dbeafe, #e0e7ff)'}}>
      {/* Dynamic Mouse-Following Gradient */}
      <div 
        className="absolute w-96 h-96 rounded-full blur-3xl opacity-10 transition-all duration-500 pointer-events-none"
        style={{
          left: mousePosition.x - 192,
          top: mousePosition.y - 192,
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 100%)'
        }}
      />

      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-30 animate-pulse" style={{background: 'linear-gradient(to right, #bfdbfe, #c4b5fd)'}}></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" style={{background: 'linear-gradient(to right, #a5f3fc, #bfdbfe)'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full animate-spin-slow opacity-20" style={{background: 'linear-gradient(to right, #e0e7ff, #f3e8ff)'}}></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute font-mono text-sm animate-float-${(i % 3) + 1} opacity-20`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.7}s`,
              color: '#60a5fa'
            }}
          >
            {['</>','{}','[]','()','&&','||','==','!='][i]}
          </div>
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          
          {/* Left Content */}
          <div className="space-y-6 lg:space-y-6 order-2 lg:order-1">
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight" style={{color: '#111827'}}>
                Web & 
                <span className=" text-2xl sm:text-3xl lg:text-4xl xl:text-5xl  block bg-gradient-to-r bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, #2563eb, #9333ea, #4f46e5)'}}>
                  Mobile App
                </span>
                <span className=" text-2xl sm:text-3xl lg:text-4xl xl:text-5xl  block" style={{color: '#1f2937'}}>
                  Development
                </span>
              </h1>
              
              {/* Animated Service Text */}
              <div className="h-12 sm:h-14 overflow-hidden rounded-xl p-3 sm:p-4 backdrop-blur-sm border shadow-lg" style={{backgroundColor: 'rgba(255, 255, 255, 0.6)', borderColor: 'rgba(147, 197, 253, 0.5)'}}>
                <div 
                  className="transition-transform duration-700 ease-out"
                  style={{ transform: `translateY(-${currentService * (window.innerWidth < 640 ? 48 : 56)}px)` }}
                >
                  {services.map((service, index) => (
                    <div key={index} className="text-lg sm:text-xl  text-center m-auto font-semibold h-12 sm:h-14 flex items-center" style={{color: '#1d4ed8'}}>
                      → {service}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-base sm:text-md lg:text-lg leading-relaxed max-w-2xl" style={{color: '#374151'}}>
              Transform your business with <span className="font-semibold" style={{color: '#2563eb'}}>cutting-edge</span> web applications, 
              mobile apps, and custom software solutions. We deliver <span className="font-semibold" style={{color: '#9333ea'}}>scalable</span>, 
              <span className="font-semibold" style={{color: '#4f46e5'}}> secure</span>, and high-performance digital experiences.
            </p>

            {/* Features Grid */}
            <div className="grid gap-2 sm:gap-2">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="group flex items-center space-x-2 sm:space-x-6 p-2 sm:p-4 rounded-xl sm:rounded-2xl backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
                  style={{backgroundColor: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(147, 197, 253, 0.5)'}}
                >
                  <div className="p-2 sm:p-3 rounded-xl shadow-lg" style={{background: 'linear-gradient(to bottom right, #3b82f6, #9333ea)'}}>
                    <feature.icon size={20} className="sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-bold mb-1" style={{color: '#111827'}}>{feature.title}</h3>
                    <p className="text-sm sm:text-base" style={{color: '#4b5563'}}>{feature.desc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{color: '#3b82f6'}} />
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 pt-2">
              <button className="w-full sm:w-auto px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 text-white font-semibold text-base sm:text-lg rounded-xl lg:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105" style={{background: 'linear-gradient(to right, #2563eb, #9333ea)'}}>
                Start Your Project
              </button>
             
            </div>
          </div>

          {/* Right Side - Code Window */}
          <div className="relative order-1 lg:order-2">
            {/* Main Code Window */}
            <div className="rounded-2xl lg:rounded-3xl backdrop-blur-sm border shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all duration-500" style={{backgroundColor: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(229, 231, 235, 0.5)'}}>
              
              {/* Window Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b" style={{borderColor: 'rgba(229, 231, 235, 0.5)', backgroundColor: 'rgba(249, 250, 251, 0.5)'}}>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full" style={{backgroundColor: '#ef4444'}}></div>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full" style={{backgroundColor: '#eab308'}}></div>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full" style={{backgroundColor: '#22c55e'}}></div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3" style={{color: '#4b5563'}}>
                  <Terminal size={16} className="sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-mono font-medium">development.js</span>
                </div>
                <button 
                  onClick={() => setIsAnimating(!isAnimating)}
                  className="p-2 rounded-lg text-white transition-colors duration-200"
                  style={{backgroundColor: '#3b82f6'}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  {isAnimating ? <Pause size={14} className="sm:w-4 sm:h-4" /> : <Play size={14} className="sm:w-4 sm:h-4" />}
                </button>
              </div>

              {/* Code Content */}
              <div className="p-4 sm:p-6 lg:p-8 font-mono text-sm sm:text-base" style={{backgroundColor: '#111827', color: '#f3f4f6'}}>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>1</span>
                    <span style={{color: '#a78bfa'}}>const</span>
                    <span style={{color: '#60a5fa'}}>solutions</span>
                    <span style={{color: '#ffffff'}}>=</span>
                    <span style={{color: '#34d399'}}>{`{`}</span>
                  </div>
                  
                  <div className="ml-8 sm:ml-12 space-y-1 sm:space-y-2">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>2</span>
                      <span style={{color: '#22d3ee'}}>webApps:</span>
                      <span style={{color: '#fbbf24'}}>'Custom & Scalable'</span>
                      <span style={{color: '#ffffff'}}>,</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>3</span>
                      <span style={{color: '#22d3ee'}}>mobileApps:</span>
                      <span style={{color: '#fbbf24'}}>'iOS & Android'</span>
                      <span style={{color: '#ffffff'}}>,</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>4</span>
                      <span style={{color: '#22d3ee'}}>ecommerce:</span>
                      <span style={{color: '#fbbf24'}}>'Full-stack'</span>
                      <span style={{color: '#ffffff'}}>,</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>5</span>
                      <span style={{color: '#22d3ee'}}>security:</span>
                      <span style={{color: '#fbbf24'}}>'Enterprise'</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>6</span>
                    <span style={{color: '#34d399'}}>{`}`}</span>
                  </div>
                  
                  <div className="mt-4 sm:mt-6 space-y-1 sm:space-y-2">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>8</span>
                      <span style={{color: '#9ca3af'}}>// Building:</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 h-6 sm:h-8 overflow-hidden">
                      <span className="text-xs w-6 text-right" style={{color: '#6b7280'}}>9</span>
                      <span style={{color: '#9ca3af'}}>//</span>
                      <div 
                        className="transition-transform duration-700 ease-out font-medium"
                        style={{ 
                          transform: `translateY(-${currentService * (window.innerWidth < 640 ? 24 : 32)}px)`,
                          color: '#67e8f9'
                        }}
                      >
                        {services.map((service, index) => (
                          <div key={index} className="h-6 sm:h-8 flex items-center">
                            {service}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Icons */}
            <div className="absolute -top-4 -right-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg animate-bounce" style={{background: 'linear-gradient(to bottom right, #22c55e, #059669)'}}>
              <Globe size={20} className="sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="absolute top-16 sm:top-20 -left-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg animate-bounce delay-500" style={{background: 'linear-gradient(to bottom right, #a855f7, #4f46e5)'}}>
              <Smartphone size={20} className="sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="absolute -bottom-4 right-8 sm:right-12 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg animate-bounce delay-1000" style={{background: 'linear-gradient(to bottom right, #3b82f6, #06b6d4)'}}>
              <Database size={20} className="sm:w-6 sm:h-6 text-white" />
            </div>

            {/* Performance Metrics Card */}
            <div className="absolute -bottom-8 -left-4 sm:-left-8 rounded-xl sm:rounded-2xl p-4 sm:p-6 backdrop-blur-sm border shadow-xl" style={{backgroundColor: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(229, 231, 235, 0.5)'}}>
              <div className="text-sm sm:text-base font-bold mb-3 sm:mb-4" style={{color: '#111827'}}>Performance</div>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm" style={{color: '#4b5563'}}>Speed</span>
                  <div className="w-16 sm:w-20 h-1.5 sm:h-2 rounded-full overflow-hidden" style={{backgroundColor: '#e5e7eb'}}>
                    <div className="h-full rounded-full animate-pulse" style={{width: '95%', background: 'linear-gradient(to right, #22c55e, #059669)'}}></div>
                  </div>
                  <span className="text-xs font-bold" style={{color: '#059669'}}>95%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm" style={{color: '#4b5563'}}>Security</span>
                  <div className="w-16 sm:w-20 h-1.5 sm:h-2 rounded-full overflow-hidden" style={{backgroundColor: '#e5e7eb'}}>
                    <div className="h-full rounded-full animate-pulse delay-500" style={{width: '98%', background: 'linear-gradient(to right, #3b82f6, #06b6d4)'}}></div>
                  </div>
                  <span className="text-xs font-bold" style={{color: '#2563eb'}}>98%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm" style={{color: '#4b5563'}}>Uptime</span>
                  <div className="w-16 sm:w-20 h-1.5 sm:h-2 rounded-full overflow-hidden" style={{backgroundColor: '#e5e7eb'}}>
                    <div className="h-full rounded-full animate-pulse delay-1000" style={{width: '99%', background: 'linear-gradient(to right, #a855f7, #4f46e5)'}}></div>
                  </div>
                  <span className="text-xs font-bold" style={{color: '#9333ea'}}>99%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animate-float-1 {
          animation: float1 6s ease-in-out infinite;
        }
        .animate-float-2 {
          animation: float2 8s ease-in-out infinite;
        }
        .animate-float-3 {
          animation: float3 10s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin 20s linear infinite;
        }
        
        @keyframes float1 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateX(0px) rotate(0deg); }
          50% { transform: translateX(30px) rotate(-180deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-15px) translateX(15px); }
          66% { transform: translateY(15px) translateX(-15px); }
        }
      `}</style>
    </div>
  );
};

export default WebDevHero;